// Regional mix helpers. Per-year shares are linearly interpolated across the
// 36-month horizon so the engine can pull a per-month region weight set
// without a step function at month 12 / 24.

export function regionKeys(inputs) {
  return Object.keys(inputs.regions);
}

/**
 * Linearly interpolate between y1, y2, y3 mixes by month (1-indexed).
 *   m=1   → y1
 *   m=12  → midway y1↔y2
 *   m=18  → y2
 *   m=30  → y3
 *   m=36  → y3
 * Each year's record sums to 1.0; interpolation preserves that.
 */
export function regionalMixForMonth(inputs, month) {
  const { y1, y2, y3 } = inputs.regionalMix;
  // Anchor points (months) for each yearly mix
  const A = 1,
    B = 18,
    C = 36;
  let mix;
  if (month <= A) mix = y1;
  else if (month >= C) mix = y3;
  else if (month <= B) mix = blend(y1, y2, (month - A) / (B - A));
  else mix = blend(y2, y3, (month - B) / (C - B));
  return mix;
}

function blend(a, b, t) {
  const out = {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) out[k] = (a[k] ?? 0) * (1 - t) + (b[k] ?? 0) * t;
  return out;
}

/**
 * Volume-weighted blended interchange rate (gross, before Wirex split) for a
 * single month based on the regional mix. Each region contributes
 * mix[k] × regions[k].interchangeRate.
 */
export function blendedRegionalInterchange(inputs, month) {
  const mix = regionalMixForMonth(inputs, month);
  let weighted = 0;
  for (const [rk, share] of Object.entries(mix)) {
    const r = inputs.regions[rk];
    if (!r) continue;
    weighted += share * r.interchangeRate;
  }
  return weighted;
}

/**
 * Volume-weighted blended FX usage rate (% of users with non-home spend in any
 * given month) based on the regional mix.
 */
export function blendedFxUsageRate(inputs, month) {
  const mix = regionalMixForMonth(inputs, month);
  let weighted = 0;
  for (const [rk, share] of Object.entries(mix)) {
    const r = inputs.regions[rk];
    if (!r) continue;
    weighted += share * r.fxUsageRate;
  }
  return weighted;
}

/**
 * Volume-weighted blended FX volume per user-with-FX (EUR/mo) for the month.
 */
export function blendedFxAvgVolume(inputs, month) {
  const mix = regionalMixForMonth(inputs, month);
  let weighted = 0;
  for (const [rk, share] of Object.entries(mix)) {
    const r = inputs.regions[rk];
    if (!r) continue;
    weighted += share * r.fxAvgVolumeMo;
  }
  return weighted;
}
